import { Component, TemplateRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../../header/header.component';
import { AdminSidebarComponent } from '../../../../../platform-admin/sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../../page-header/page-header.component';
import { StepperComponent, StepperStep } from '../../../primeng/stepper/stepper.component';
import { MetadataInformationComponent, MetadataFormData } from '../metadata-information/metadata-information.component';
import { UsecaseInformationComponent, ParsedSection, UsecaseSection } from '../usecase-information/usecase-information.component';
import { CatalogueApiService } from '@angular-workspace/services';
import { ConfirmationDialogComponent } from '../../../confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-usecases-home',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
    MetadataInformationComponent,
    UsecaseInformationComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './usecases-home.component.html',
  styleUrl: './usecases-home.component.scss',
})
export class UsecasesHomeComponent implements AfterViewInit {
  @ViewChild('generalInfoTemplate', { static: true }) generalInfoTemplate!: TemplateRef<any>;
  @ViewChild('usecaseInfoTemplate', { static: true }) usecaseInfoTemplate!: TemplateRef<any>;

  constructor(
    private router: Router,
    private catalogueApiService: CatalogueApiService
  ) {}

  steps: StepperStep[] = [];
  currentStep: number = 0;

  // Form data - Step 1 (Metadata Information)
  metadataFormData: MetadataFormData = {
    title: '',
    description: '',
    shortDescription: '',
    selectedIndustries: [],
    selectedOrganisationTypes: [],
    accessPolicy: '',
    tags: [],
  };

  // Validation errors
  validationErrors: { [key: string]: string } = {};

  // Parsed content from Step 2
  parsedContent: ParsedSection[] = [];

  // Raw sections from Step 2 (preserved when switching steps)
  usecaseSections: UsecaseSection[] = [];

  // Success modal state
  showSuccessModal = false;
  createdUsecaseId: string | null = null;

  // Title availability (must be 'available' to proceed to step 2)
  titleAvailabilityStatus: 'checking' | 'available' | 'not-available' | null = null;

  ngAfterViewInit(): void {
    this.steps = [
      {
        key: 'general-info',
        label: 'Metadata Information',
        content: this.generalInfoTemplate,
      },
      {
        key: 'usecase-info',
        label: 'Use Case Information',
        content: this.usecaseInfoTemplate,
      },
    ];
  }

  onStepChange(stepIndex: number): void {
    this.currentStep = stepIndex;
  }

  onMetadataFormDataChange(formData: MetadataFormData): void {
    this.metadataFormData = { ...formData };
  }

  onMetadataValidationErrorsChange(errors: { [key: string]: string }): void {
    this.validationErrors = { ...errors };
  }

  onUsecaseSectionsChange(sections: UsecaseSection[]): void {
    this.usecaseSections = [...sections];
  }

  onUsecaseParsedContentChange(parsedContent: ParsedSection[]): void {
    this.parsedContent = [...parsedContent];
  }

  onUsecaseValidationErrorsChange(errors: { [key: string]: string }): void {
    this.validationErrors = { ...this.validationErrors, ...errors };
  }

  onTitleAvailabilityChange(status: 'checking' | 'available' | 'not-available' | null): void {
    this.titleAvailabilityStatus = status;
  }

  validateGeneralInfo(): boolean {
    // Delegate validation to MetadataInformationComponent
    const hasErrors = !this.metadataFormData.title ||
                      !this.metadataFormData.accessPolicy ||
                      !this.metadataFormData.shortDescription ||
                      !this.metadataFormData.description ||
                      !this.metadataFormData.selectedIndustries?.length ||
                      !this.metadataFormData.selectedOrganisationTypes?.length ||
                      !this.metadataFormData.tags?.length;
    // Require title to be available (no duplicate) before proceeding to step 2
    const titleAvailable = this.titleAvailabilityStatus === 'available';
    return !hasErrors && titleAvailable;
  }

  validateUsecaseInfo(): boolean {
    // Delegate validation to UsecaseInformationComponent
    // For now, check if parsed content exists
    return this.parsedContent && this.parsedContent.length > 0;
  }

  async submitUsecase(): Promise<void> {
    try {
      // Build payload from form data
      const payload = this.buildPayload();
      
      // Debug: Log the payload being sent
      console.log('Payload being sent to API:', JSON.stringify(payload, null, 2));
      
      // Call the API
      const response = await this.catalogueApiService.createUseCase(payload);
      
      console.log('Usecase created successfully:', response);
      
      // Extract usecase ID from response
      // The ID might be in response.id, response.result.id, response.data.id, etc.
      let usecaseId: string | null = null;
      if (response) {
        usecaseId = response['id'] || 
                   response['result']?.['id'] || 
                   response['data']?.['id'] ||
                   response['result']?.['result']?.['id'] ||
                   response['itemId'] ||
                   response['result']?.['itemId'] ||
                   null;
      }
      
      // Store the usecase ID and show success modal
      this.createdUsecaseId = usecaseId;
      this.showSuccessModal = true;
    } catch (error) {
      console.error('Error creating usecase:', error);
      // Error toaster will be shown by the API service
    }
  }

  onViewUsecase(): void {
    if (this.createdUsecaseId) {
      this.router.navigate([`/usecase/${this.createdUsecaseId}`]);
    }
    this.showSuccessModal = false;
  }

  onCreateNewUsecase(): void {
    // Reset form data
    this.metadataFormData = {
      title: '',
      description: '',
      shortDescription: '',
      selectedIndustries: [],
      selectedOrganisationTypes: [],
      accessPolicy: '',
      tags: [],
    };
    this.parsedContent = [];
    this.usecaseSections = [];
    this.validationErrors = {};
    this.currentStep = 0;
    this.createdUsecaseId = null;
    
    // Navigate to add-contributions
    this.router.navigate(['/admin/add-contributions']);
  }

  onSuccessModalClose(): void {
    this.showSuccessModal = false;
    this.createdUsecaseId = null;
  }

  private buildPayload(): any {
    // Extract values from multiselect arrays (they might be objects with label/value or just strings)
    const industries = this.metadataFormData.selectedIndustries.map((item: any) => 
      typeof item === 'string' ? item : (item.value || item.label || item)
    );
    const organisationTypes = this.metadataFormData.selectedOrganisationTypes.map((item: any) => 
      typeof item === 'string' ? item : (item.value || item.label || item)
    );

    // Map access policy for API: PUBLIC -> OPEN, others uppercase
    const rawPolicy = (this.metadataFormData.accessPolicy || 'OPEN').toUpperCase().trim();
    const accessPolicy = rawPolicy === 'PUBLIC' ? 'OPEN' : rawPolicy;

    // Get user email from localStorage (stored by auth-listener service)
    let uploadedBy = 'cos-user forestdx-stg@datakaveri.org'; // Default fallback
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const result = userData['result'] || userData;
        const email = result['email'] || '';
        if (email) {
          uploadedBy = `cos-user ${email}`;
        }
      }
    } catch (error) {
      console.warn('Could not get user email from localStorage:', error);
    }

    // Map sections from parsedContent to API format
    const sections = this.parsedContent
      .filter(section => section.title && section.title.trim().length > 0) // Only include sections with valid titles
      .map(section => {
        const sectionData: any = {
          title: (section.title || '').trim(),
        };

        // Always include content, even if empty
        if (section.content) {
          sectionData.content = String(section.content).trim();
        } else {
          sectionData.content = '';
        }

        if (section.subsections && section.subsections.length > 0) {
          sectionData.subsections = section.subsections.map(sub => ({
            subtitle: (sub.subtitle || '').trim(),
            content: Array.isArray(sub.points) ? sub.points.join(' ').trim() : String(sub.points || '').trim()
          }));
        }

        return sectionData;
      });

    // Ensure tags are strings and trim them
    const tags = Array.isArray(this.metadataFormData.tags) 
      ? this.metadataFormData.tags.map(tag => String(tag || '').trim()).filter(tag => tag.length > 0)
      : [];

    // Build payload with trimmed values
    const payload: any = {
      type: ['adex:Apps'],
      name: (this.metadataFormData.title || '').trim(),
      tags: tags,
      label: (this.metadataFormData.title || '').trim(),
      description: (this.metadataFormData.description || '').trim(),
      shortDescription: (this.metadataFormData.shortDescription || '').trim(),
      organizationType: organisationTypes.length > 0 ? String(organisationTypes[0] || '').trim() : '',
      department: industries.length > 0 ? String(industries[0] || '').trim() : '',
      uploadedBy: String(uploadedBy || '').trim(),
      fileFormat: 'csv',
      accessPolicy: (accessPolicy || 'OPEN').trim(),
      industry: industries.length > 0 ? String(industries[0] || '').trim() : '',
      sections: Array.isArray(sections) && sections.length > 0 ? sections : [],
      canBeUsedFor: {
        "Early Risk Stratification": "Early detection of disease risk in crops",
        "Field-Level Prioritisation": "Helps prioritize treatment at field level"
      },
      dataInputsAndAiTechniques: {
        "Input Data Banks": [
          "Leaf image datasets"
        ],
        "Expected Output": [
          "Disease classification",
          "Treatment suggestions"
        ],
        "Models": [
          "CNN",
          "ResNet",
          "MobileNet"
        ],
        "Potential Benefits": [
          "Improved crop yield",
          "Reduced pesticide usage"
        ]
      }
    };

    return payload;
  }

  onPrevious(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  hasError(fieldName: string): boolean {
    return !!this.validationErrors[fieldName];
  }

  getError(fieldName: string): string {
    return this.validationErrors[fieldName] || '';
  }

  private isSubmitting = false; // Flag to prevent duplicate submissions

  onNext(): void {
    // Prevent duplicate submissions
    if (this.isSubmitting) {
      console.warn('Submission already in progress, ignoring duplicate click');
      return;
    }

    // Validate current step before proceeding
    if (this.currentStep === 0) {
      if (!this.validateGeneralInfo()) {
        return; // Don't proceed if validation fails
      }
    } else if (this.currentStep === 1) {
      if (!this.validateUsecaseInfo()) {
        return; // Don't proceed if validation fails
      }
      // If on last step, submit the form
      this.isSubmitting = true;
      this.submitUsecase().finally(() => {
        this.isSubmitting = false;
      });
      return;
    }

    // Proceed to next step if validation passes
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      // Clear validation errors when moving to next step
      this.validationErrors = {};
    }
  }
}
